/**
 * GDSPreviewModal.js
 * Handles the creation, animation, and interaction for the product image preview modal.
 */
import { getSizedShopifyUrl, formatMoney } from './GDSModules.js';
import { GDSScrollLockManager } from './GDSScrollLockManager.js';

export class GDSPreviewModal {
    constructor(modalManager) {
        this.modalManager = modalManager;
        this.builder = modalManager.getBuilder();
        this.swiperInstances = {};
        this._addLoadingStyles();
        this._addIconFixStyles();
    }

    /**
     * Opens the preview modal for a given product handle.
     * @param {string} productHandle - The handle of the product to display.
     */
    async open(productHandle) {
        let clickedButton = null;

        try {
            clickedButton = document.querySelector(`.preview-eye-btn[data-product-handle="${productHandle}"]`);

            if (clickedButton) {
                this._showButtonLoading(clickedButton, true);
            }

            const product = await this.builder.apiService.fetchProductData(productHandle);
            if (!product) throw new Error("Product data could not be loaded.");

            const variantId = product.variants?.[0]?.id;
            const variantMetafields = variantId ? await this.builder.apiService.fetchVariantMetafields(variantId) : null;

            document.querySelector('.gds-preview-modal')?.remove();

            const modalHTML = this._generateHTML(product, variantMetafields);
            document.body.insertAdjacentHTML('beforeend', modalHTML);

            const modal = document.querySelector('.gds-preview-modal');

            this.modalManager.openModal(modal);
            this._setupModal(modal);

        } catch (error) {
            this.builder.uiFeedback.showError(error.message);
        } finally {
            if (clickedButton) {
                this._showButtonLoading(clickedButton, false);
            }
        }
    }

    /**
     * Closes the provided preview modal with the correct animation.
     * @param {HTMLElement} modal - The modal element to close.
     */
    close(modal) {
        if (!modal) return;

        Object.values(this.swiperInstances).forEach(swiper => swiper?.destroy(true, true));
        this.swiperInstances = {};

        const backdrop = modal.querySelector('.preview-modal-backdrop');
        const desktopContent = modal.querySelector('.preview-modal-content');
        const mobileContent = modal.querySelector('.mobile-preview-content');

        modal.style.opacity = '0';
        modal.style.pointerEvents = 'none';
        if (backdrop) backdrop.style.opacity = '0';

        if (window.innerWidth >= 768 && desktopContent) {
            desktopContent.style.transform = 'scale(0.95)';
        } else if (mobileContent) {
            // Reverted to original: Animate the modal down and out of view (slide-up close).
            mobileContent.style.transform = 'translateY(100%)';
        }

        setTimeout(() => {
            modal.remove();
            this.modalManager.activeModals.delete(modal);
            if (this.modalManager.activeModals.size === 0) {
                GDSScrollLockManager.unlock();
            }
        }, 300);
    }

    /**
     * Sets up the initial state and triggers the opening animation for the modal.
     * @param {HTMLElement} modal - The newly created modal element.
     * @private
     */
    _setupModal(modal) {
        const backdrop = modal.querySelector('.preview-modal-backdrop');
        const desktopContent = modal.querySelector('.preview-modal-content');
        const mobileContent = modal.querySelector('.mobile-preview-content');

        void modal.offsetHeight;

        modal.style.opacity = '1';
        modal.style.pointerEvents = 'auto';
        if (backdrop) backdrop.style.opacity = '1';

        if (window.innerWidth >= 768 && desktopContent) {
            desktopContent.style.opacity = '1';
            desktopContent.style.transform = 'scale(1)';
        } else if (mobileContent) {
            mobileContent.style.opacity = '1';
            // Reverted to original: Animate modal into view from the bottom.
            mobileContent.style.transform = 'translateY(0)';
            // MODIFICATION: Set up the swipe-to-close gesture for mobile.
            this._setupSwipeToClose(modal, mobileContent);
        }

        this._initializeSwiper(modal);
    }

    /**
     * MODIFICATION:
     * This new method adds the swipe-to-close functionality to the mobile preview modal,
     * replicating the logic from the original GDSBuilder class.
     * @param {HTMLElement} modal - The top-level modal element.
     * @param {HTMLElement} mobileContent - The element that slides (.mobile-preview-content).
     * @private
     */
    _setupSwipeToClose(modal, mobileContent) {
        const dragHandle = mobileContent.querySelector('.w-full.py-3.flex.justify-center');
        const imageArea = mobileContent.querySelector('.w-full.aspect-square');
        const scrollableContent = mobileContent.querySelector('.overflow-y-auto');

        let startY = 0;
        let currentY = 0;
        let isDragging = false;
        let startTime = 0;

        const onTouchStart = (e) => {
            // Only start dragging if the touch is on the handle, image, or the content is scrolled to the top.
            const isAtTop = scrollableContent ? scrollableContent.scrollTop === 0 : true;
            if (!e.target.closest('.w-full.py-3.flex.justify-center') && !e.target.closest('.w-full.aspect-square') && !isAtTop) {
                return;
            }

            startY = e.touches[0].clientY;
            currentY = startY;
            startTime = Date.now();
            isDragging = true;
            mobileContent.style.transition = 'none'; // Remove transition for smooth dragging
        };

        const onTouchMove = (e) => {
            if (!isDragging) return;

            currentY = e.touches[0].clientY;
            const deltaY = currentY - startY;

            // Only allow dragging downward.
            if (deltaY > 0) {
                e.preventDefault();
                mobileContent.style.transform = `translateY(${deltaY}px)`;
            }
        };

        const onTouchEnd = () => {
            if (!isDragging) return;
            isDragging = false;

            const deltaY = currentY - startY;
            const deltaTime = Date.now() - startTime;
            const velocity = deltaTime > 0 ? deltaY / deltaTime : 0;

            // Close if dragged more than 1/4 of the screen height, or if a fast flick occurs.
            const shouldClose = deltaY > (window.innerHeight / 4) || (velocity > 0.5 && deltaY > 50);

            mobileContent.style.transition = 'transform 0.3s ease-out'; // Re-apply transition

            if (shouldClose) {
                this.close(modal);
            } else {
                // Snap back to the open position.
                mobileContent.style.transform = 'translateY(0)';
            }
        };

        // Add listeners to the specified elements
        [dragHandle, imageArea].forEach(el => {
            if (el) {
                el.addEventListener('touchstart', onTouchStart, { passive: true });
            }
        });

        // Add move/end listeners to the whole content area to track the gesture anywhere on screen
        mobileContent.addEventListener('touchmove', onTouchMove, { passive: false });
        mobileContent.addEventListener('touchend', onTouchEnd);
        mobileContent.addEventListener('touchcancel', onTouchEnd);
    }

    /**
     * Initializes Swiper instances for the image galleries.
     * @private
     */
    _initializeSwiper(modal) {
        const isMobile = window.innerWidth < 768;
        const mainSelector = isMobile ? '.preview-main-gallery-mobile' : '.preview-main-gallery';
        const thumbsSelector = isMobile ? '.preview-thumbnails-gallery-mobile' : '.preview-thumbnails-gallery';
        const hasMultipleImages = modal.querySelectorAll(`${mainSelector} .swiper-slide`).length > 1;

        if (!hasMultipleImages) return;

        let thumbsSwiper = null;
        const thumbsEl = modal.querySelector(thumbsSelector);
        if (thumbsEl) {
            thumbsSwiper = new Swiper(thumbsEl, {
                spaceBetween: 8,
                slidesPerView: 'auto',
                freeMode: true,
                watchSlidesProgress: true,
                allowTouchMove: true,
                navigation: {
                    nextEl: isMobile ? '.thumbnail-button-next-mobile' : '.thumbnail-button-next',
                    prevEl: isMobile ? '.thumbnail-button-prev-mobile' : '.thumbnail-button-prev',
                },
            });
            this.swiperInstances.thumbs = thumbsSwiper;
        }

        const mainSwiper = new Swiper(modal.querySelector(mainSelector), {
            effect: 'fade',
            fadeEffect: { crossFade: true },
            allowTouchMove: true,
            loop: true,
            pagination: { el: '.preview-pagination-mobile', clickable: true },
            navigation: { nextEl: '.preview-button-next', prevEl: '.preview-button-prev' },
            thumbs: { swiper: thumbsSwiper },
        });
        this.swiperInstances.main = mainSwiper;
    }

    /**
     * Generates the complete HTML structure for the preview modal.
     * @private
     */
    _generateHTML(product, variantMetafields = null) {
        const variant = product.variants?.[0] || {};
        const variantAvailable = variant.available || false;
        const formattedPrice = formatMoney(product.price || 0);
        const compareAtPrice = variant.compare_at_price ? formatMoney(variant.compare_at_price) : null;
        const sku = variant.sku || product.handle;
        const upc = variant.barcode || '';
        const modalId = `preview-modal-${product.id}-${Date.now()}`;
        const isOnSale = variant.compare_at_price && variant.compare_at_price > (product.price || 0);

        const overview = this._getMetafieldValue(variantMetafields, 'overview') || product.description || '';
        const patentStatus = this._getMetafieldValue(variantMetafields, 'patented') || '';

        const media = product.media || product.images || [];
        const hasMultipleImages = media.length > 1;
        const hasMoreThanFourImages = media.length > 4;
        const hasMoreThanFiveImages = media.length > 5;

        const mainGallerySlides = this._generateMainSlides(product, media);
        const thumbnailSlides = hasMultipleImages ? this._generateThumbSlides(product, media) : '';

        const overviewContent = this._generateOverviewContent(overview);
        const featuresContent = this._generateFeaturesContent(variantMetafields);
        const specsContent = this._generateSpecsContent(variantMetafields);
        const productLinkHTML = this._generateProductLinkHTML(product.handle);

        const desktopModalContentHTML = `
            <button type="button" class="modal-close absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors duration-200 bg-white rounded-full p-2 shadow-md">
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            <!-- Responsive image container using Mount Builder scaling -->
            <div class="image-container w-full flex-shrink-0 bg-gray-50
                        h-64 sm:h-72 md:h-80 lg:h-96 xl:h-[28rem] 2xl:h-[32rem]
                        min-h-[16rem] max-h-[45vh]">
                <div class="swiper preview-main-gallery w-full h-full">
                    <div class="swiper-wrapper">${mainGallerySlides}</div>
                    ${hasMultipleImages ? `<div class="swiper-button-next preview-button-next"></div><div class="swiper-button-prev preview-button-prev"></div>` : ''}
                </div>
                ${!variantAvailable ? `<div class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20"><div class="bg-gray-800 text-white px-4 py-2 rounded-full transform -rotate-12 font-bold text-lg shadow-lg">Coming Soon</div></div>` : ''}
            </div>

            ${hasMultipleImages ? `<div class="p-2 md:p-3 lg:p-4 flex-shrink-0 bg-gray-50"><div class="swiper preview-thumbnails-gallery ${hasMoreThanFiveImages ? 'carousel-mode' : ''}"><div class="swiper-wrapper">${thumbnailSlides}</div>${hasMoreThanFiveImages ? `<div class="swiper-button-next thumbnail-button-next"></div><div class="swiper-button-prev thumbnail-button-prev"></div>` : ''}</div></div>` : ''}

            <!-- Responsive content area with flex-grow -->
            <div class="p-4 md:p-5 lg:p-6 overflow-y-auto flex-grow min-h-[200px]">
                ${patentStatus ? `<div class="mb-3"><span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-[#00B588]">${patentStatus}</span></div>` : ''}
                <h3 class="text-base md:text-lg font-medium font-heading leading-tight text-gray-900">${product.title}</h3>
                <div class="text-sm text-gray-600 mt-2 space-y-1">
                    <div class="flex items-center"><span class="font-medium">Part#:</span><span class="ml-1 font-light">${sku}</span></div>
                    ${upc ? `<div class="flex items-center"><span class="font-medium">UPC:</span><span class="ml-1 font-light">${upc}</span></div>` : ''}
                    ${(product.price || 0) > 0 ? `<div class="flex items-center ${isOnSale ? 'pb-3' : ''}"><div class="py-1 text-xl ${isOnSale ? 'text-red-600' : 'text-gray-900'}">${formattedPrice}</div>${isOnSale && compareAtPrice ? `<div class="py-1 text-xl pl-3 pt-2 text-gray-500 line-through">${compareAtPrice}</div>` : ''}</div>` : ''}
                </div>
                ${productLinkHTML}
                ${overviewContent}
                ${featuresContent}
                ${specsContent}
            </div>
        `;

        const mobileModalContentHTML = `
            <div class="w-full py-3 flex justify-center"><div class="w-12 h-1 bg-gray-300 rounded-full"></div></div>
            <div class="overflow-y-auto" style="max-height: calc(90vh - 60px); overscroll-behavior: contain;">
                <div class="w-full aspect-square bg-gray-50 flex items-center justify-center relative">
                    <div class="swiper preview-main-gallery-mobile w-full h-full">
                        <div class="swiper-wrapper">${mainGallerySlides}</div>
                        ${hasMultipleImages ? `<div class="swiper-pagination preview-pagination-mobile"></div>` : ''}
                    </div>
                    ${!variantAvailable ? `<div class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20"><div class="bg-red-600 text-white px-3 py-1 rounded-full transform -rotate-12 font-bold text-sm shadow-lg">Coming Soon</div></div>` : ''}
                </div>
                ${hasMultipleImages ? `<div class="px-4 py-2 bg-gray-50"><div class="swiper preview-thumbnails-gallery-mobile ${hasMoreThanFourImages ? 'carousel-mode' : ''}"><div class="swiper-wrapper">${thumbnailSlides}</div>${hasMoreThanFourImages ? `<div class="swiper-button-next thumbnail-button-next-mobile"></div><div class="swiper-button-prev thumbnail-button-prev-mobile"></div>` : ''}</div></div>` : ''}
                <div class="p-4">
                    ${patentStatus ? `<div class="mb-3"><span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-[#00B588]">${patentStatus}</span></div>` : ''}
                    <div class="product-title text-lg font-bold font-heading leading-tight">${product.title}</div>
                    <div class="sku-barcode text-sm space-y-2">
                        <div class="flex items-center"><span class="font-medium">Part#:</span><span class="ml-1 font-light">${sku}</span></div>
                        ${upc ? `<div class="flex items-center"><span class="font-medium">UPC:</span><span class="ml-1 font-light">${upc}</span></div>` : ''}
                        ${(product.price || 0) > 0 ? `<div class="flex items-center ${isOnSale ? 'pb-3' : ''} mb-3"><div class="py-1 text-lg ${isOnSale ? 'text-red-600' : 'text-gray-900'}">${formattedPrice}</div>${isOnSale && compareAtPrice ? `<div class="py-1 text-lg pl-3 pt-2 text-gray-500 line-through">${compareAtPrice}</div>` : ''}</div>` : ''}
                    </div>
                    ${productLinkHTML}
                    ${overviewContent}
                    ${featuresContent}
                    ${specsContent}
                </div>
            </div>
        `;

        return `
            <div id="${modalId}" class="fixed inset-0 z-[60] gds-preview-modal preview-modal" style="opacity: 0; pointer-events: none;">
                <div class="absolute inset-0 preview-modal-backdrop" data-backdrop="true" style="opacity: 0; background: rgba(0, 0, 0, 0.6);"></div>
                <div class="hidden md:flex items-center justify-center min-h-screen p-4">
                    <div class="preview-modal-content relative bg-white
                                w-full max-w-xl lg:max-w-2xl xl:max-w-3xl 2xl:max-w-4xl
                                mx-auto rounded-lg shadow-2xl overflow-hidden flex flex-col
                                max-h-[90vh] opacity-0 scale-95">
                        ${desktopModalContentHTML}
                    </div>
                </div>
                <div class="block md:hidden">
                    <div class="fixed inset-x-0 bottom-0 transform mobile-preview-content" style="opacity: 0; transform: translateY(100%); overscroll-behavior: contain;">
                        <div class="bg-white rounded-t-3xl shadow-xl w-full max-h-[90vh] overflow-hidden">
                            ${mobileModalContentHTML}
                        </div>
                    </div>
                </div>
                <style>
                    /* Override for viewport height breakpoints */
                    @media (max-height: 768px) {
                        .gds-preview-modal .image-container {
                            max-height: 40vh !important;
                        }
                    }

                    @media (min-height: 1080px) {
                        .gds-preview-modal .image-container {
                            max-height: 500px !important;
                        }
                    }

                    /* Ensure proper flex behavior */
                    .gds-preview-modal .preview-modal-content {
                        display: flex !important;
                        flex-direction: column !important;
                    }

                    /* Image container responsive behavior */
                    .gds-preview-modal .image-container {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .gds-preview-modal .image-container img {
                        max-width: 100%;
                        max-height: 100%;
                        object-fit: contain;
                    }

                    /* Ensure swiper slides fill container */
                    .gds-preview-modal .swiper-slide {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        height: 100%;
                    }

                    .gds-preview-modal .swiper-slide img {
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                    }

                    /* Thumbnails responsive sizing */
                    @media (max-height: 768px) {
                        .preview-thumbnails-gallery {
                            height: 50px !important;
                        }

                        .preview-thumbnails-gallery .swiper-slide {
                            width: 50px !important;
                            height: 50px !important;
                        }
                    }

                    /* Transition classes for modal animations */
                    .gds-preview-modal {
                        transition: opacity 0.3s ease;
                    }

                    .preview-modal-backdrop {
                        transition: opacity 0.3s ease;
                    }

                    .preview-modal-content {
                        transition: opacity 0.3s ease, transform 0.3s ease;
                    }

                    .mobile-preview-content {
                        transition: transform 0.3s ease-out, opacity 0.3s ease-out;
                    }

                    /* See more/less button styling */
                    .gds-see-more-btn {
                        color: #00B588;
                        font-weight: 500;
                        background: none;
                        border: none;
                        padding: 0;
                        cursor: pointer;
                        transition: color 0.2s ease;
                    }

                    .gds-see-more-btn:hover {
                        color: #009670;
                        text-decoration: underline;
                    }

                    /* Product link styling */
                    .gds-product-link {
                        color: #00B588;
                        transition: color 0.2s ease;
                    }

                    .gds-product-link:hover {
                        color: #009670;
                        text-decoration: underline;
                    }

                    .gds-product-link svg {
                        transition: transform 0.2s ease;
                    }

                    .gds-product-link:hover svg {
                        transform: translateX(2px);
                    }

                    .preview-main-gallery, .preview-main-gallery-mobile { width: 100%; height: 100%; }
                    .preview-thumbnails-gallery, .preview-thumbnails-gallery-mobile { height: 70px; padding: 10px 0; overflow: hidden; position: relative; min-height: 70px; opacity: 0; transition: opacity 0.2s 0.1s ease-in-out; }
                    .preview-thumbnails-gallery.swiper-initialized, .preview-thumbnails-gallery-mobile.swiper-initialized { opacity: 1; }
                    .preview-thumbnails-gallery .swiper-wrapper, .preview-thumbnails-gallery-mobile .swiper-wrapper { transition-property: transform; transition-timing-function: ease; transition-duration: 300ms; }
                    .preview-thumbnails-gallery .swiper-slide, .preview-thumbnails-gallery-mobile .swiper-slide { width: 60px; height: 60px; margin: 0 4px; flex-shrink: 0; }
                    .preview-thumbnails-gallery.carousel-mode { padding: 0 35px; }
                    .preview-thumbnails-gallery-mobile.carousel-mode { padding: 0 30px; overflow: hidden; }
                    .preview-thumbnails-gallery.carousel-mode .swiper-slide, .preview-thumbnails-gallery-mobile.carousel-mode .swiper-slide { width: 60px; height: 60px; margin: 0 4px; flex-shrink: 0; }
                    .preview-thumbnails-gallery-mobile.carousel-mode .swiper-wrapper { width: auto; }
                    .preview-thumbnails-gallery .swiper-slide div, .preview-thumbnails-gallery-mobile .swiper-slide div { height: 100%; width: 100%; border: 2px solid transparent; transition: border-color 0.3s ease; display: flex; align-items: center; justify-content: center; overflow: hidden; border-radius: 4px; }
                    .preview-thumbnails-gallery .swiper-slide-thumb-active div, .preview-thumbnails-gallery-mobile .swiper-slide-thumb-active div { border-color: #00B588 !important; }
                    .preview-thumbnails-gallery .swiper-slide img, .preview-thumbnails-gallery-mobile .swiper-slide img { max-width: 100%; max-height: 100%; object-fit: contain; }
                    .preview-button-next, .preview-button-prev { color: #00B588; background: white; border-radius: 50%; box-shadow: 0 2px 8px rgba(0,0,0,0.1); width: 40px; height: 40px; }
                    .preview-button-next:after, .preview-button-prev:after { font-size: 16px; font-weight: bold; }
                    .thumbnail-button-next, .thumbnail-button-prev, .thumbnail-button-next-mobile, .thumbnail-button-prev-mobile { color: #00B588; background: white; border-radius: 50%; box-shadow: 0 2px 8px rgba(0,0,0,0.1); width: 24px; height: 24px; margin-top: 0; top: 50%; transform: translateY(-50%); z-index: 10; }
                    .thumbnail-button-next:after, .thumbnail-button-prev:after, .thumbnail-button-next-mobile:after, .thumbnail-button-prev-mobile:after { font-size: 10px; font-weight: bold; }
                    .thumbnail-button-next, .thumbnail-button-next-mobile { right: 5px; }
                    .thumbnail-button-prev, .thumbnail-button-prev-mobile { left: 5px; }
                    .preview-pagination-mobile { position: relative !important; bottom: auto !important; margin-top: 10px !important; }
                    .preview-pagination-mobile .swiper-pagination-bullet { background-color: #8D8D8D; opacity: 1; }
                    .preview-pagination-mobile .swiper-pagination-bullet-active { background-color: #00B588; }
                    @media (max-width: 768px) {
                        .mobile-preview-content { touch-action: none; overscroll-behavior: contain; transition: transform 0.3s ease-out, opacity 0.3s ease-out; }
                        .mobile-preview-content .w-full.py-3.flex.justify-center { cursor: grab; user-select: none; -webkit-user-select: none; }
                        .mobile-preview-content .modal-close { display: none; }
                    }
                </style>
            </div>
        `;
    }

    // --- Private Helper Methods ---

    _getMetafieldValue(metafields, key) {
        if (!metafields || !metafields.metafields) return '';
        const metafield = metafields.metafields.find(mf => mf.namespace === 'variant_attrs' && mf.key === key);
        return metafield ? metafield.value : '';
    }

    _generateMainSlides(product, media) {
        if (!media?.length) {
            return `<div class="swiper-slide"><div class="w-full h-full flex items-center justify-center"><div class="text-gray-400 text-center"><svg class="w-16 h-16 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" /></svg><p>No image available</p></div></div></div>`;
        }
        return media
            .filter(item => item.media_type === 'image')
            .map(mediaItem => `
                <div class="swiper-slide">
                    <img src="${getSizedShopifyUrl(mediaItem.src, 500, 500)}" alt="${(mediaItem.alt || product.title).replace(/"/g, '&quot;')}" class="w-full h-full object-contain">
                </div>
            `).join('');
    }

    _generateThumbSlides(product, media) {
        if (!media?.length) return '';
        return media
            .filter(item => item.media_type === 'image')
            .map(mediaItem => `
                <div class="swiper-slide cursor-pointer">
                    <div class="w-full h-full bg-gray-50 rounded overflow-hidden border-2 border-transparent hover:border-gray-300 transition-colors">
                        <img src="${getSizedShopifyUrl(mediaItem.src, 80, 80)}" alt="${(mediaItem.alt || product.title).replace(/"/g, '&quot;')} thumbnail" class="w-full h-full object-contain">
                    </div>
                </div>
            `).join('');
    }

    _cleanOverviewText(text) {
        if (!text) return '';
        let cleaned = text.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n\n').replace(/<[^>]*>/g, '');
        cleaned = cleaned.replace(/^\s+|\s+$/g, '');
        return cleaned.replace(/\n/g, '<br>'); // Convert back to <br> for HTML display
    }

    /**
     * Generate product link HTML with icon
     */
    _generateProductLinkHTML(productHandle) {
        if (!productHandle) return '';
        const productUrl = `${window.Shopify?.routes?.root || '/'}products/${productHandle}`;

        return `
            <div class="mt-2">
                <a href="${productUrl}"
                   target="_blank"
                   rel="noopener noreferrer"
                   class="gds-product-link inline-flex items-center text-sm font-medium">
                    View Full Product Details
                    <svg class="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                    </svg>
                </a>
            </div>
        `;
    }

    _generateOverviewContent(overview) {
        if (!overview) return '';
        const cleanOverview = this._cleanOverviewText(overview);
        const averageCharsPerLine = 80;
        const maxLines = 3;
        const lines = Math.ceil(cleanOverview.replace(/<br>/g, '').length / averageCharsPerLine);

        if (lines <= maxLines) {
            return `<div class="mt-4"><h4 class="text-sm font-medium text-gray-900 mb-2">Overview</h4><div class="text-sm text-gray-600 leading-relaxed">${cleanOverview}</div></div>`;
        } else {
            const truncatedLength = Math.floor(maxLines * averageCharsPerLine);
            const truncatedText = cleanOverview.substring(0, truncatedLength);
            const finalTruncated = truncatedText.substring(0, truncatedText.lastIndexOf(' '));
            const overviewId = `overview-${Math.random().toString(36).substring(2, 9)}`;
            return `<div class="mt-4">
                <h4 class="text-sm font-medium text-gray-900 mb-2">Overview</h4>
                <div class="text-sm text-gray-600 leading-relaxed">
                    <div id="${overviewId}-truncated">${finalTruncated}... <button type="button" class="gds-see-more-btn" onclick="this.parentElement.style.display='none'; this.parentElement.nextElementSibling.style.display='block';">See more</button></div>
                    <div id="${overviewId}-full" style="display: none;">${cleanOverview} <button type="button" class="gds-see-more-btn" onclick="this.parentElement.style.display='none'; this.parentElement.previousElementSibling.style.display='block';">See less</button></div>
                </div>
            </div>`;
        }
    }

    _generateFeaturesContent(variantMetafields) {
        const featuresMetafield = this._getMetafieldValue(variantMetafields, 'features');
        if (!featuresMetafield) return '';
        try {
            const featuresData = JSON.parse(featuresMetafield);
            const featuresArray = Object.keys(featuresData).filter(key => key.startsWith('feature_') && featuresData[key]).map(key => featuresData[key]);
            if (featuresArray.length === 0) return '';
            const featuresHtml = featuresArray.map(feature => `<li class="flex items-start text-sm text-gray-600 leading-relaxed"><span class="text-green-500 mr-2 mt-1">•</span><span>${feature}</span></li>`).join('');
            return `<div class="mt-4 pt-4"><h4 class="text-sm font-medium text-gray-900 mb-3">Features</h4><ul class="space-y-2">${featuresHtml}</ul></div>`;
        } catch (e) {
            console.warn('Error parsing features metafield:', e);
            return '';
        }
    }

    _generateSpecsContent(variantMetafields) {
        const specsMetafield = this._getMetafieldValue(variantMetafields, 'specs');
        if (!specsMetafield) return '';
        try {
            const specsData = JSON.parse(specsMetafield);
            const specsEntries = Object.entries(specsData);
            if (specsEntries.length === 0) return '';
            const formatLabel = (key) => key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, l => l.toUpperCase());
            const specsHtml = specsEntries.map(([key, value]) => `<div class="bg-gray-50 rounded-lg p-3"><dt class="text-sm font-medium text-gray-700 mb-1">${formatLabel(key)}</dt><dd class="text-sm text-gray-600">${String(value).replace(/\|/g, '<br>')}</dd></div>`).join('');
            return `<div class="mt-4 pt-4">
                <button type="button" class="w-full flex items-center justify-between text-left" onclick="this.closest('div').querySelector('.specs-content').classList.toggle('hidden'); this.querySelector('.specs-toggle-icon').classList.toggle('rotate-45');">
                    <h4 class="text-sm font-medium text-gray-900">Specifications</h4>
                    <svg class="w-4 h-4 text-gray-600 transition-transform duration-200 specs-toggle-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                    </svg>
                </button>
                <div class="specs-content hidden mt-3 space-y-3">${specsHtml}</div>
            </div>`;
        } catch (e) {
            console.warn('Error parsing specs metafield:', e);
            return '';
        }
    }

    _addLoadingStyles() {
        if (document.getElementById('gds-preview-loading-styles')) return;

        const style = document.createElement('style');
        style.id = 'gds-preview-loading-styles';
        style.textContent = `
            @keyframes gds-spinner-spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
            .gds-spinner {
                animation: gds-spinner-spin 1s linear infinite;
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * @param {HTMLElement} button - The button element to modify.
     * @param {boolean} isLoading - Whether to show or hide the loading state.
     * @private
     */
    _showButtonLoading(button, isLoading) {
        if (!button) return;
        const container = button.closest('.preview-eye-icon');

        if (isLoading) {
            if (!button._originalContent) {
                button._originalContent = button.innerHTML;
            }
            button.innerHTML = `
                <svg class="w-8 h-8 text-gray-600 gds-spinner" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            `;
            // Add the 'preview-loading' class to the button to trigger your pulse animation
            button.classList.add('preview-loading');
            // Also add a loading class to the container for desktop hover states
            if (container) {
                container.classList.add('preview-loading');
            }
            button.disabled = true;
        } else {
            if (button._originalContent) {
                button.innerHTML = button._originalContent;
                delete button._originalContent;
            }
            button.classList.remove('preview-loading');
             if (container) {
                container.classList.remove('preview-loading');
            }
            button.disabled = false;
        }
    }

  _addIconFixStyles() {
      // Check if the style element already exists to avoid duplication
      if (document.getElementById('gds-icon-fix-styles')) return;

      const style = document.createElement('style');
      style.id = 'gds-icon-fix-styles';
      style.textContent = `
          /*
          * This rule forcibly sets the fill color of any <circle> element
          * inside the preview button's SVG to white. This is intended to
          * restore the icon's background.
          */
          .preview-eye-btn svg circle {
              fill: white !important;
          }

          /*
          * This rule ensures the parts of the eye itself (the pupil and iris)
          * remain a darker color and are not affected by the rule above.
          * It targets paths, which are commonly used for detailed shapes.
          */
          .preview-eye-btn svg path {
              fill: #4A5568 !important; /* A medium gray color */
          }

          /*
          * This rule ensures the button container itself remains transparent
          * and does not have a border or shadow.
          */
          .preview-eye-btn {
              background: transparent !important;
              border: none !important;
              box-shadow: none !important;
          }
      `;
      document.head.appendChild(style);
  }
}
